import { Button, Divider, message, Space, Typography, Flex, Card } from "antd";
import { useEffect, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import GeneralUploader from "../pdfEditor/Uploader";
import { downloadImage } from "../pdfEditor/utils";

const { Title, Text } = Typography;

const PREVIEW_WIDTH = 400;
const PREVIEW_HEIGHT = 400;

const canvasToBlob = (canvas, type = "image/jpeg", quality = 0.92) =>
    new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
            type,
            quality
        );
    });

const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

export default function ImageCrop() {
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ unit: "%", width: 0, height: 0 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [processedDataUrl, setProcessedDataUrl] = useState(null);
    const imageRef = useRef(null);

    const processed = Boolean(processedDataUrl);

    useEffect(() => {
        return () => previewUrl && URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const resetAll = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setFile(null);
        setImageSrc(null);
        setCrop({ unit: "%", width: 0, height: 0 });
        setCompletedCrop(null);
        setProcessedDataUrl(null);
    };

    const handleUploadChange = (info) => {
        const uploaded = Array.isArray(info) ? info[0] : info?.file ?? info;
        if (!uploaded) return;

        const actual = uploaded.originFileObj ?? uploaded;
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }

        const objectUrl = URL.createObjectURL(actual);
        setFile(actual);
        setPreviewUrl(objectUrl);
        setImageSrc(objectUrl);
        setCompletedCrop(null);
        setCrop({ unit: "%", width: 0, height: 0 });
        setProcessedDataUrl(null);
    };

    const handleProcess = async () => {
        if (
            !imageRef.current ||
            !completedCrop ||
            !completedCrop.width ||
            !completedCrop.height
        ) {
            message.error("Please select a crop area first");
            return;
        }

        try {
            const img = imageRef.current;
            const scaleX = img.naturalWidth / img.width;
            const scaleY = img.naturalHeight / img.height;

            const sx = Math.round(completedCrop.x * scaleX);
            const sy = Math.round(completedCrop.y * scaleY);
            const sw = Math.round(completedCrop.width * scaleX);
            const sh = Math.round(completedCrop.height * scaleY);

            const canvas = document.createElement("canvas");
            canvas.width = sw;
            canvas.height = sh;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

            const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
            const dataUrl = await blobToDataUrl(blob);

            setProcessedDataUrl(dataUrl);
            message.success("Image processed");
        } catch (err) {
            console.error(err);
            message.error("Processing failed");
        }
    };

    const handleRedownload = () => {
        if (!processedDataUrl)
            return message.error(
                "No processed image available. Please Crop first."
            );
        const baseName = file?.name?.replace(/\.[^/.]+$/, "") ?? "image";
        downloadImage(processedDataUrl, `${baseName}_cropped.jpg`);
    };

    const handleChangeImage = resetAll;

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Space
                direction="vertical"
                size={40}
                style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    width: "100%",
                }}
            >
                <Flex vertical align="center">
                    <Title level={1}>Crop Image</Title>
                    <Text>Crop images to focus on important areas</Text>
                </Flex>

                {!imageSrc && (
                    <GeneralUploader
                        showUploadList={false}
                        beforeUpload={() => false}
                        accept="image/*"
                        multiple={false}
                        onChange={handleUploadChange}
                    />
                )}

                {imageSrc && !processed && (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Flex
                            vertical
                            align="center"
                            gap="middle"
                            style={{ width: "40%", height: "auto" }}
                        >
                            <ReactCrop
                                crop={crop}
                                onChange={setCrop}
                                onComplete={(c) => setCompletedCrop(c)}
                            >
                                <img
                                    ref={imageRef}
                                    src={imageSrc}
                                    alt="Crop target"
                                    style={{
                                        objectFit: "contain",
                                        display: "block",
                                    }}
                                />
                            </ReactCrop>

                            <Divider style={{ margin: "20px 0" }} />

                            <Space>
                                <Button type="primary" onClick={handleProcess}>
                                    Crop
                                </Button>
                                <Button onClick={handleChangeImage}>
                                    Change Image
                                </Button>
                            </Space>
                        </Flex>
                    </div>
                )}

                {processed && (
                    <Card
                        size="small"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your image is ready
                        </Title>

                        <Flex vertical gap={14} style={{ marginTop: 20 }}>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleRedownload}
                                style={{ minWidth: 220 }}
                            >
                                Download
                            </Button>
                            <Button
                                size="default"
                                onClick={handleChangeImage}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </Flex>
                    </Card>
                )}
            </Space>
        </div>
    );
}
